'use client';

import React, { useState } from 'react';
import { Button } from '../../components/ui';
import { showErrorToast, showInfoToast } from '../../components/Toast';
import { apiErrorMessage } from '../../utils/apiError';
import { useApiClient } from '../../contexts/ApiContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { UPGRADE_FEE } from '../../queries/useUpgradeQueries';
import type { AvailableUpgradeTargetItem } from '../../services/api';
import { OrderEnum } from '../../services/generated/Api';
import { cn } from '../../utils/cn';
import translations from './UpgradeScreen.translations';

export const UPGRADE_PRESET_MULTIPLIERS = [4 / 3, 1.5, 2, 3, 5] as const;

const PAGE_SIZE = 20;

interface Cursor {
  multiplier: number;
  sourceKey: string;
  index: number;
  banded: boolean;
  hashName: string;
}

interface UpgradePresetsProps {
  sourceItemId: string | null;
  sourceAmount: number | null;
  selectedHashName: string | null;
  onPick: (item: AvailableUpgradeTargetItem) => void;
  disabled?: boolean;
  className?: string;
}

const formatMultiplier = (m: number) => `x${Number.isInteger(m) ? m : m.toFixed(2).replace(/0$/, '')}`;
const chanceOf = (m: number) => (1 - UPGRADE_FEE) / m;
const formatChance = (m: number) => `${Math.round(chanceOf(m) * 100)}%`;

export const UpgradePresets: React.FC<UpgradePresetsProps> = ({
  sourceItemId,
  sourceAmount,
  selectedHashName,
  onPick,
  disabled = false,
  className,
}) => {
  const { t } = useLocalTranslation(translations);
  const apiClient = useApiClient();
  const [cursor, setCursor] = useState<Cursor | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  const sourceKey = `${sourceItemId ?? ''}+${sourceAmount ?? 0}`;

  const fetchAt = async (multiplier: number, index: number, banded: boolean) => {
    const next = UPGRADE_PRESET_MULTIPLIERS[UPGRADE_PRESET_MULTIPLIERS.indexOf(multiplier) + 1];
    const response = await apiClient.api.upgradeControllerGetAvailableUpgradeTargets({
      sourceItemId: sourceItemId ?? '',
      sourceAmount: sourceAmount ?? undefined,
      maxProbability: chanceOf(multiplier),
      minProbability: banded && next ? chanceOf(next) : undefined,
      order: OrderEnum.Chance,
      page: Math.floor(index / PAGE_SIZE) + 1,
      limit: PAGE_SIZE,
    });
    return response.items[index % PAGE_SIZE] ?? null;
  };

  const pick = async (multiplier: number) => {
    if (!sourceItemId || pending !== null) return;
    const continues =
      cursor?.multiplier === multiplier && cursor.sourceKey === sourceKey && cursor.hashName === selectedHashName;
    let index = continues ? cursor.index + 1 : 0;
    let banded = continues ? cursor.banded : true;

    setPending(multiplier);
    try {
      let item = await fetchAt(multiplier, index, banded);
      if (!item && index > 0) {
        index = 0;
        item = await fetchAt(multiplier, 0, banded);
      }
      if (!item && banded) {
        banded = false;
        item = await fetchAt(multiplier, 0, false);
      }
      if (!item) {
        showInfoToast(t('presetsEmpty'), undefined, 'upgrade-presets-empty');
        return;
      }
      setCursor({ multiplier, sourceKey, index, banded, hashName: item.hashName });
      onPick(item);
    } catch (error) {
      showErrorToast(t('errorLoadTargets'), apiErrorMessage(error), 'upgrade-presets-error');
    } finally {
      setPending(null);
    }
  };

  const activeMultiplier =
    cursor && cursor.sourceKey === sourceKey && cursor.hashName === selectedHashName ? cursor.multiplier : null;
  const inactive = disabled || !sourceItemId;

  return (
    <div
      className={cn('flex min-w-0 flex-col gap-1.5', className)}
      data-testid="upgrade-presets"
    >
      <p className="m-0 text-[13px] text-label-secondary">{t('presetsLabel')}</p>
      <div className="grid grid-cols-5 gap-1.5">
        {UPGRADE_PRESET_MULTIPLIERS.map((m) => {
          const active = activeMultiplier === m;
          return (
            <Button
              key={m}
              size="s"
              variant={active ? 'primary' : 'tertiary'}
              disabled={inactive}
              loading={pending === m}
              onClick={() => void pick(m)}
              aria-pressed={active}
              data-testid={`upgrade-preset-${m.toFixed(2)}`}
              className={cn(
                'h-11 min-w-0 flex-col gap-0 rounded-2xl px-1',
                active ? 'border border-transparent' : 'border border-white/[0.08] bg-white/[0.04]',
              )}
            >
              <span className="text-[15px] font-bold leading-[18px] tabular-nums">{formatMultiplier(m)}</span>
              <span className={cn('text-[11px] leading-[13px] tabular-nums', active ? 'text-white/80' : 'text-white/45')}>
                {formatChance(m)}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
